import React, { useEffect, useState } from "react";
import { Modal, Button, Tooltip, Space, Row, Col, Spin, Typography, Card, message, Empty, Pagination } from "antd";
import { CheckCircleOutlined, StarFilled, PlayCircleOutlined, PauseCircleOutlined } from "@ant-design/icons";
import { HeyGenAvatar } from "../utils/VideoGeneration/helperFunctions/tavusApi";
import useGetHeyGenAvatars from "@src/api/useGetHeyGenAvatars";
import './replicaSelectionModal.less';

const { Text, Title } = Typography;

// Props for ReplicaSelectionModal
interface ReplicaSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectReplica: (replicaId: string, previewUrl: string, voiceId?: string, avatarProvider?: 'tavus' | 'heygen') => void;
  selectedReplicaId?: string;
  avatarSelectionLoading?: boolean;
}




export const ReplicaSelectionModal: React.FC<ReplicaSelectionModalProps> = ({ 
  isOpen, 
  onClose, 
  onSelectReplica,
  avatarSelectionLoading = false
}) => {
  // Premium avatar states (HeyGen)
  const [selectedHeyGenAvatar, setSelectedHeyGenAvatar] = useState<HeyGenAvatar | null>(null);
  const [playingVideoId, setPlayingVideoId] = useState<string | null>(null);
  const [heygenOffset, setHeygenOffset] = useState<number>(0);
  const heygenLimit = 20;
  
  // Use React Query for HeyGen avatars with caching
  const {
    data: heygenAvatars,
    isLoading: heygenLoading,
    pagination: heygenPagination
  } = useGetHeyGenAvatars({
    limit: heygenLimit,
    offset: heygenOffset
  });
  
  // Reset playing video when modal opens
  useEffect(() => {
    if (isOpen) {
      setPlayingVideoId(null);
    }
  }, [isOpen]);

  const handleSelectReplica = () => {
    if (!selectedHeyGenAvatar) {
      message.warning('Please select an avatar');
      return;
    }
    // For HeyGen avatars, don't pass voiceId (no voice selection needed)
    onSelectReplica(
      selectedHeyGenAvatar.avatar_id,
      selectedHeyGenAvatar.preview_video_url,
      undefined, // No voiceId for HeyGen
      'heygen'
    );
  };
  
  const handleHeyGenAvatarClick = (avatar: HeyGenAvatar) => {
    setSelectedHeyGenAvatar(avatar);
  };
  
  const handleHeyGenVideoPlay = (id: string, _url: string, e: React.MouseEvent) => {
    e.stopPropagation();
    
    if (playingVideoId === id) {
      const video = document.getElementById(`heygen-video-${id}`) as HTMLVideoElement;
      if (video) {
        video.pause();
      }
      setPlayingVideoId(null);
    } else {
      if (playingVideoId) {
        const currentVideo = document.getElementById(`heygen-video-${playingVideoId}`) as HTMLVideoElement;
        if (currentVideo) {
          currentVideo.pause();
        }
      }
      
      const video = document.getElementById(`heygen-video-${id}`) as HTMLVideoElement;
      if (video) {
        video.play().catch(err => console.error("Video play error:", err));
      }
      setPlayingVideoId(id);
    }
  };

   
  return (
    <Modal
      title={
        <div className="replica-modal-header">
          <Title level={4}>Choose an Actor</Title>
          <Space>
            <Button
              type="primary"
              onClick={handleSelectReplica}
              icon={<CheckCircleOutlined />}
              disabled={!selectedHeyGenAvatar || avatarSelectionLoading}
              loading={avatarSelectionLoading}
            >
              Use Selected
            </Button>
            {/* <Button
              type="text"
              icon={<CloseOutlined />}
              onClick={onClose}
            /> */}
          </Space>
        </div>
      }
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={1000}
      className="replica-selection-modal"
      styles={{ 
        body: { maxHeight: '80vh', overflow: 'auto', padding: '16px' } 
      }}
      destroyOnClose={true}
    >
      <div className="replica-selection-content">
        {heygenLoading ? (
          <div className="loading-container">
            <Spin size="large" />
          </div>
        ) : heygenAvatars.length === 0 ? (
          <div className="empty-container">
            <Empty description="No premium avatars found" />
          </div>
        ) : (
          <>
            <div className="replicas-grid">
              <Row gutter={[16, 24]}>
                {heygenAvatars.map((avatar, index) => {
                  const colSizes = { xs: 24, sm: 12, md: 8, lg: 6 };
                  
                  return (
                    <Col 
                      key={avatar.avatar_id || index}
                      {...colSizes}
                    >
                      <Card
                        className={`replica-card ${selectedHeyGenAvatar?.avatar_id === avatar.avatar_id ? 'selected' : ''}`}
                        onClick={() => handleHeyGenAvatarClick(avatar)}
                        styles={{ 
                          body: { padding: '12px', height: '100%', display: 'flex', flexDirection: 'column' } 
                        }}
                        hoverable
                        cover={null}
                      >
                        <div className="video-container-wrapper" style={{ position: 'relative', flex: 1 }} onClick={() => handleHeyGenAvatarClick(avatar)}>
                          <div className="video-container">
                            {!avatar.preview_video_url && <Spin className="video-loader" />}
                            <video
                              id={`heygen-video-${avatar.avatar_id}`}
                              className="video-player"
                              src={avatar.preview_video_url}
                              muted
                              playsInline
                              loop
                              preload="metadata"
                              onLoadStart={(e) => {
                                const target = e.currentTarget;
                                target.style.opacity = '0';
                              }}
                              onCanPlay={(e) => {
                                const target = e.currentTarget;
                                target.style.opacity = '1';
                              }}
                            />
                            <div 
                              className="play-overlay"
                              onClick={(e) => handleHeyGenVideoPlay(avatar.avatar_id, avatar.preview_video_url, e)}
                            >
                              {playingVideoId === avatar.avatar_id ? (
                                <PauseCircleOutlined className="play-icon" />
                              ) : (
                                <PlayCircleOutlined className="play-icon" />
                              )}
                            </div>
                          </div>
                          {selectedHeyGenAvatar?.avatar_id === avatar.avatar_id && (
                            <div className="selection-indicator" style={{ 
                              position: 'absolute', 
                              bottom: '8px', 
                              left: '8px', 
                              background: 'rgba(24, 144, 255, 0.8)', 
                              color: 'white', 
                              padding: '2px 8px', 
                              borderRadius: '12px',
                              fontSize: '12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                              zIndex: 2
                            }}>
                              <CheckCircleOutlined /> Selected
                            </div>
                          )}
                        </div>
                        <div className="replica-title" style={{ marginTop: '8px', height: '22px' }}>
                          {avatar.premium && (
                            <Tooltip title="Premium Actor">
                              <StarFilled className="premium-star" style={{ color: '#faad14', marginRight: '4px' }} />
                            </Tooltip>
                          )}
                          <Tooltip title={avatar.avatar_name}>
                            <Text 
                              strong 
                              className="replica-name"
                              style={{ 
                                width: '100%', 
                                textAlign: 'center', 
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block'
                              }}
                            >
                              {avatar.avatar_name || "Unnamed Actor"}
                            </Text>
                          </Tooltip>
                        </div>
                      </Card>
                    </Col>
                  );
                })}
              </Row>
            </div>
            {heygenPagination && heygenPagination.has_more && (
              <div style={{ marginTop: '24px', display: 'flex', justifyContent: 'center' }}>
                <Pagination
                  current={Math.floor(heygenOffset / heygenLimit) + 1}
                  total={heygenPagination.total}
                  pageSize={heygenLimit}
                  onChange={(page) => {
                    setHeygenOffset((page - 1) * heygenLimit);
                    setPlayingVideoId(null);
                  }}
                  showSizeChanger={false}
                />
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};



export default ReplicaSelectionModal;






